import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, ChevronRight, User, LogOut, Heart, Settings } from 'lucide-react';
import { categories, deals, stores } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import AuthModal from './AuthModal';
import ProfileModal from './ProfileModal';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ deals: [], stores: [], categories: [] });
  const [mobileSearchResults, setMobileSearchResults] = useState({ deals: [], stores: [], categories: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearchResults, setShowMobileSearchResults] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, loading } = useAuth();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Categories', path: '/categories', hasDropdown: true },
    { name: 'All stores', path: '/stores' },
    { name: 'Contact', path: '/contact' },
    { name: 'Blog', path: '/blog' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCategoriesOpen(false);
    setMobileSearchQuery('');
    setShowMobileSearchResults(false);
  }, [location.pathname]);

  // Desktop search functionality
  useEffect(() => {
    if (searchQuery.trim().length > 1) {
      const query = searchQuery.toLowerCase();
      
      const matchedDeals = deals.filter(deal => 
        deal.title.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query)
      ).slice(0, 5);

      const matchedStores = stores.filter(store =>
        store.name.toLowerCase().includes(query)
      ).slice(0, 3);

      const matchedCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(query)
      ).slice(0, 3);

      setSearchResults({ deals: matchedDeals, stores: matchedStores, categories: matchedCategories });
      setShowSearchResults(true);
    } else {
      setSearchResults({ deals: [], stores: [], categories: [] });
      setShowSearchResults(false);
    }
  }, [searchQuery]);

  // Mobile search functionality
  useEffect(() => {
    if (mobileSearchQuery.trim().length > 1) {
      const query = mobileSearchQuery.toLowerCase();
      
      const matchedDeals = deals.filter(deal => 
        deal.title.toLowerCase().includes(query) ||
        deal.description?.toLowerCase().includes(query)
      ).slice(0, 4);

      const matchedStores = stores.filter(store =>
        store.name.toLowerCase().includes(query)
      ).slice(0, 3);

      const matchedCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(query)
      ).slice(0, 3);

      setMobileSearchResults({ deals: matchedDeals, stores: matchedStores, categories: matchedCategories });
      setShowMobileSearchResults(true);
    } else {
      setMobileSearchResults({ deals: [], stores: [], categories: [] });
      setShowMobileSearchResults(false);
    }
  }, [mobileSearchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/stores?search=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchQuery('');
    }
  };

  const handleMobileSearchSubmit = (e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/stores?search=${encodeURIComponent(mobileSearchQuery)}`);
      setMobileMenuOpen(false);
      setMobileSearchQuery('');
      setShowMobileSearchResults(false);
    }
  };

  const handleMobileResultClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setMobileSearchQuery('');
    setShowMobileSearchResults(false);
  };

  const handleOpenAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    setMobileMenuOpen(false);
  };

  const hasResults = searchResults.deals?.length > 0 || 
                     searchResults.stores?.length > 0 || 
                     searchResults.categories?.length > 0;

  const hasMobileResults = mobileSearchResults.deals?.length > 0 || 
                           mobileSearchResults.stores?.length > 0 || 
                           mobileSearchResults.categories?.length > 0;

  return (
    <>
      <header className="bg-[#000000] sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center h-16">
            {/* Mobile Menu Button - Show on mobile and tablet */}
            <button
              className="xl:hidden text-white p-2 hover:bg-white/10 rounded transition-colors mr-2"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <img src="/logo.png" alt="V" className="w-8 h-8 rounded" />
              <div className="flex flex-col">
                <span className="text-white font-bold text-lg xl:text-xl">Vans Shoes Coupon Club</span>
                <span className="text-white/80 text-xs hidden xl:block">Grab Daily New Deals!</span>
              </div>
            </Link>

            {/* Desktop Navigation - Centered - Only show on XL screens */}
            <nav className="hidden xl:flex items-center justify-center flex-1 mx-4">
              <div className="flex items-center space-x-1">
                {navItems.map((item) => (
                  <div key={item.name} className="relative group">
                    {item.hasDropdown ? (
                      <button
                        className={`flex items-center px-3 py-2 text-white font-medium text-sm hover:bg-white/10 rounded transition-colors ${
                          isActive(item.path) ? 'border-b-2 border-yellow-400' : ''
                        }`}
                        onMouseEnter={() => setCategoriesOpen(true)}
                        onMouseLeave={() => setCategoriesOpen(false)}
                      >
                        {item.name.toUpperCase()}
                        <ChevronRight size={16} className="ml-1 rotate-90" />
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`px-3 py-2 text-white font-medium text-sm hover:bg-white/10 rounded transition-colors block ${
                          isActive(item.path) ? 'border-b-2 border-yellow-400' : ''
                        }`}
                      >
                        {item.name.toUpperCase()}
                      </Link>
                    )}

                    {/* Categories Dropdown */}
                    {item.hasDropdown && (
                      <div
                        className={`absolute top-full left-0 bg-white rounded-lg shadow-xl py-2 w-56 transition-all duration-200 ${
                          categoriesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                        }`}
                        onMouseEnter={() => setCategoriesOpen(true)}
                        onMouseLeave={() => setCategoriesOpen(false)}
                      >
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                            className="block px-4 py-2 text-gray-700 hover:bg-gray-100 text-sm transition-colors"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to="/privacy-policy"
                  className="px-3 py-2 text-white font-medium text-sm hover:bg-white/10 rounded transition-colors block"
                >
                  PRIVACY POLICY
                </Link>
              </div>
            </nav>

            {/* Spacer for tablet/mobile to push search/auth to the right */}
            <div className="flex-1 xl:hidden"></div>

            {/* Search and Auth - Right side */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {/* Desktop Search Bar */}
              <div className="hidden md:block relative">
                <form onSubmit={handleSearchSubmit} className="flex items-center bg-white rounded-md overflow-hidden shadow-sm">
                  <input
                    type="text"
                    placeholder="Search deals..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.length > 1 && setShowSearchResults(true)}
                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                    className="px-3 py-2 text-sm w-40 lg:w-48 focus:outline-none"
                    data-testid="desktop-search-input"
                  />
                  <button type="submit" className="bg-[#000000] px-3 py-2 hover:bg-[#000000] transition-colors">
                    <Search size={18} className="text-white" />
                  </button>
                </form>

                {/* Desktop Search Results Dropdown */}
                {showSearchResults && hasResults && (
                  <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto animate-fadeIn">
                    {searchResults.categories?.length > 0 && (
                      <div className="p-2 border-b">
                        <p className="text-xs text-gray-500 font-medium px-2 mb-1">CATEGORIES</p>
                        {searchResults.categories.map(cat => (
                          <Link
                            key={cat.id}
                            to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                            className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.stores?.length > 0 && (
                      <div className="p-2 border-b">
                        <p className="text-xs text-gray-500 font-medium px-2 mb-1">STORES</p>
                        {searchResults.stores.map(store => (
                          <Link
                            key={store.id}
                            to={`/store/${store.slug}`}
                            className="block px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            {store.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {searchResults.deals?.length > 0 && (
                      <div className="p-2">
                        <p className="text-xs text-gray-500 font-medium px-2 mb-1">DEALS</p>
                        {searchResults.deals.map(deal => (
                          <Link
                            key={deal.id}
                            to={`/store/${deal.store}`}
                            className="flex items-center gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            onClick={() => setShowSearchResults(false)}
                          >
                            <img src={deal.image} alt="" className="w-8 h-8 object-cover rounded" />
                            <div className="flex-1 min-w-0">
                              <p className="truncate">{deal.title}</p>
                              <p className="text-xs text-gray-800">${deal.salePrice?.toFixed(2)}</p>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Mobile Search */}
              <button 
                className="md:hidden text-white p-2 hover:bg-white/10 rounded transition-colors"
                onClick={() => setMobileMenuOpen(true)}
              >
                <Search size={22} />
              </button>

              {/* Desktop Auth Buttons */}
              {!loading && (
                <div className="hidden md:flex items-center space-x-2">
                  {isAuthenticated ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="flex items-center space-x-2 text-white px-3 py-1.5 hover:bg-white/10 rounded transition-colors"
                        data-testid="user-menu-btn"
                      >
                        <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                          <span className="text-gray-800 font-bold text-sm">
                            {user?.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium hidden lg:inline">{user?.name?.split(' ')[0]}</span>
                        <ChevronRight size={16} className={`transition-transform ${showUserMenu ? 'rotate-90' : ''}`} />
                      </button>

                      {/* User Dropdown */}
                      {showUserMenu && (
                        <div 
                          className="absolute top-full right-0 mt-1 w-52 bg-white rounded-lg shadow-xl border z-50 py-2 animate-fadeIn"
                          onMouseLeave={() => setShowUserMenu(false)}
                        >
                          <div className="px-4 py-2 border-b">
                            <p className="text-sm font-medium text-gray-800 truncate">{user?.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                          </div>
                          <Link
                            to="/favorites"
                            onClick={() => setShowUserMenu(false)}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            data-testid="favorites-link"
                          >
                            <Heart size={16} />
                            My Favorites
                            {user?.favorites?.length > 0 && (
                              <span className="ml-auto bg-gray-200 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                                {user.favorites.length}
                              </span>
                            )}
                          </Link>
                          <button
                            onClick={() => {
                              setShowUserMenu(false);
                              setShowProfileModal(true);
                            }}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            data-testid="profile-settings-btn"
                          >
                            <Settings size={16} />
                            Account Settings
                          </button>
                          <div className="border-t my-1"></div>
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            data-testid="logout-btn"
                          >
                            <LogOut size={16} />
                            Sign Out
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => handleOpenAuth('login')}
                        className="text-white text-sm font-medium px-3 py-1.5 hover:bg-white/10 rounded transition-colors"
                        data-testid="login-btn"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => handleOpenAuth('register')}
                        className="bg-yellow-400 text-black text-sm font-bold px-4 py-1.5 rounded hover:bg-yellow-300 transition-colors"
                        data-testid="register-btn"
                      >
                        Sign Up
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-50 xl:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Slide-out Drawer */}
      <div 
        className={`fixed top-0 left-0 h-full w-72 bg-white z-50 transform transition-transform duration-300 ease-in-out xl:hidden flex flex-col ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Drawer Header */}
        <div className="bg-[#000000] p-4 flex items-center justify-between">
          <span className="text-white font-bold">Menu</span>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="text-white p-1 hover:bg-white/10 rounded transition-colors"
            data-testid="close-menu-btn"
          >
            <X size={24} />
          </button>
        </div>

        {/* Mobile User Section */}
        {!loading && (
          <div className="p-4 border-b bg-gray-50">
            {isAuthenticated ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#000000] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm truncate">{user?.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link
                    to="/favorites"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-white border border-white/30 rounded text-sm font-medium hover:bg-white/10 transition-colors"
                    data-testid="mobile-favorites-link"
                  >
                    <Heart size={16} />
                    Favorites
                    {user?.favorites?.length > 0 && (
                      <span className="bg-black text-white text-xs px-1.5 rounded-full">
                        {user.favorites.length}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowProfileModal(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#000000] text-white rounded text-sm font-medium hover:bg-[#000000] transition-colors"
                    data-testid="mobile-settings-btn"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-center gap-2 py-2 text-red-600 border border-red-200 rounded text-sm font-medium hover:bg-red-50 transition-colors"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => handleOpenAuth('login')}
                  className="flex-1 py-2 text-white border border-white/30 rounded font-medium text-sm hover:bg-white/10 transition-colors"
                  data-testid="mobile-login-btn"
                >
                  Sign In
                </button>
                <button
                  onClick={() => handleOpenAuth('register')}
                  className="flex-1 py-2 bg-[#000000] text-white rounded font-medium text-sm hover:bg-[#000000] transition-colors"
                  data-testid="mobile-register-btn"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search in Drawer with Suggestions */}
        <div className="p-4 border-b relative">
          <form onSubmit={handleMobileSearchSubmit}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search deals, stores..."
                value={mobileSearchQuery}
                onChange={(e) => setMobileSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#000000]"
                data-testid="mobile-search-input"
              />
              <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-800">
                <Search size={20} />
              </button>
            </div>
          </form>

          {/* Mobile Search Results */}
          {showMobileSearchResults && hasMobileResults && (
            <div className="absolute left-4 right-4 top-full mt-1 bg-white rounded-lg shadow-xl border z-50 max-h-64 overflow-y-auto animate-fadeIn">
              {mobileSearchResults.categories?.length > 0 && (
                <div className="p-2 border-b">
                  <p className="text-xs text-gray-500 font-medium px-2 mb-1">CATEGORIES</p>
                  {mobileSearchResults.categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => handleMobileResultClick(cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`)}
                      className="block w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
              {mobileSearchResults.stores?.length > 0 && (
                <div className="p-2 border-b">
                  <p className="text-xs text-gray-500 font-medium px-2 mb-1">STORES</p>
                  {mobileSearchResults.stores.map(store => (
                    <button
                      key={store.id}
                      onClick={() => handleMobileResultClick(`/store/${store.slug}`)}
                      className="block w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      {store.name}
                    </button>
                  ))}
                </div>
              )}
              {mobileSearchResults.deals?.length > 0 && (
                <div className="p-2">
                  <p className="text-xs text-gray-500 font-medium px-2 mb-1">DEALS</p>
                  {mobileSearchResults.deals.map(deal => (
                    <button
                      key={deal.id}
                      onClick={() => handleMobileResultClick(`/store/${deal.store}`)}
                      className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
                    >
                      <img src={deal.image} alt="" className="w-8 h-8 object-cover rounded" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-xs">{deal.title}</p>
                        <p className="text-xs text-gray-800 font-medium">${deal.salePrice?.toFixed(2)}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Links */}
        <nav className="overflow-y-auto flex-1">
          {navItems.map((item) => (
            <div key={item.name}>
              {item.hasDropdown ? (
                <>
                  <button
                    onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-700 font-medium hover:bg-gray-100 border-b transition-colors"
                  >
                    <span>{item.name}</span>
                    <ChevronRight 
                      size={20} 
                      className={`text-gray-400 transition-transform duration-200 ${mobileCategoriesOpen ? 'rotate-90' : ''}`} 
                    />
                  </button>
                  <div className={`bg-gray-50 border-b overflow-hidden transition-all duration-300 ${
                    mobileCategoriesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={cat.slug === 'blog' ? '/blog' : `/category/${cat.slug}`}
                        className="block px-8 py-2.5 text-gray-600 text-sm hover:bg-gray-100 hover:text-gray-800 transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.path}
                  className="block px-4 py-3 text-gray-700 font-medium hover:bg-gray-100 border-b transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
          
          {/* Privacy Policy Link */}
          <Link
            to="/privacy-policy"
            className="block px-4 py-3 text-gray-700 font-medium hover:bg-gray-100 border-b transition-colors"
            onClick={() => setMobileMenuOpen(false)}
          >
            Privacy Policy
          </Link>
        </nav>

        {/* App Info Footer */}
        <div className="border-t bg-gray-50 p-4">
          <div className="text-center space-y-1">
            <p className="text-xs text-gray-500 font-medium">Vans Shoes Coupon Club</p>
            <div className="flex items-center justify-center gap-3 text-xs text-gray-400">
              <span>Version 1.0.0</span>
              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
              <span>Build 2026.01.16</span>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">© 2025 marks.inx. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </>
  );
};

export default Header;
